import {CheckoutEventsItem} from "@paddle/paddle-js";
import {action, computed, makeObservable, observable} from "mobx";
import intl from "react-intl-universal";

import type {PricingComputation} from "../../pricing";
import type {SeatUsage} from "../../../services/SeatManager";
import {
    CheckoutContentProps,
    CheckoutInput, CheckoutPeriod,
    PaymentGateway,
    PriceCurrency,
    PriceDetails,
    ProrationSummary,
    ValidationError
} from "../../types";
import {SeatManager} from "../../../services/SeatManager";
import {SUBSCRIPTION_PLANS} from "../../subscription_plans";

import {getInitialSeatsCount} from "../getInitialUsage";
import {calculateRubPricing, calculateUsdPricing} from "../../pricing";

import {logErrorToSentry} from "../../../utils/errors/logErrorToSentry";
import {ComponentType} from "react";
import {ClientInApp} from "../../../shared/types/SubscriptionTypes";
import {WsServerConnection} from "@stores/serverConnection/wsServerConnection";
import {UserSessionState} from "@stores/UserSessionState";


export abstract class SeatCheckoutGatewayBase implements PaymentGateway {
    client: ClientInApp;
    seatUsage: SeatUsage;
    userState: UserSessionState
    checkoutInput: CheckoutInput;
    totals: PriceDetails = {subtotal: 0, tax: 0, total: 0};
    items: CheckoutEventsItem[] = [];
    showSuccessPayment = false;
    isLoading = false;
    isProcessingUpgrade = false;
    canShowTax = false;
    abstract CheckoutContent: ComponentType<CheckoutContentProps>;
    priceCurrency: PriceCurrency = 'USD';
    prorationDueNow: ProrationSummary | null = null;
    isProrationLoading = false;
    prorationError: string | null = null;
    private prorationRequestId = 0;
    private initialCheckoutValues: {
        locationSlots: number;
        userSeats: number;
        purchasedLocationSlots: number;
        purchasedUserSeats: number;
        period: CheckoutPeriod;
    };

    constructor(userState: UserSessionState, client: ClientInApp, seatUsage: SeatUsage, currency: PriceCurrency = 'USD') {
        this.client = client;
        this.userState = userState;
        this.seatUsage = seatUsage;

        this.initialCheckoutValues = getInitialSeatsCount(client, seatUsage);
        this.priceCurrency = currency;

        this.checkoutInput = {
            plan: SUBSCRIPTION_PLANS.STANDARD,
            locationSlotsCount: this.initialCheckoutValues.locationSlots,
            userSeatsCount: this.initialCheckoutValues.userSeats,
            period: this.initialCheckoutValues.period,
        };

        makeObservable<this, 'setTotals' | 'setProrationSummary' | 'setProrationLoading' | 'setProrationError' | 'setProcessingUpgrade' | 'setShowSuccessPayment'>(this, {
            seatUsage: observable,
            checkoutInput: observable,
            totals: observable,
            items: observable,
            showSuccessPayment: observable,
            isLoading: observable,
            canShowTax: observable,
            prorationDueNow: observable,
            isProrationLoading: observable,
            isProcessingUpgrade: observable,
            prorationError: observable,
            billableUserSeats: computed,
            validationErrors: computed,
            canProceed: computed,
            hasChanges: computed,
            isUpgradeFlow: computed,
            setCheckoutInput: action,
            setIsLoading: action,
            setCanShowTax: action,
            setTotals: action,
            setProrationSummary: action,
            setProrationLoading: action,
            setProcessingUpgrade: action,
            setProrationError: action,
            setShowSuccessPayment: action,
        });

        this.configureInitialState();
        this.recalculatePricing();
    }

    async init(): Promise<void> {
    }

    renderCheckout(): void {}

    destroy?(): void;

    setIsLoading = (newValue: boolean) => {
        this.isLoading = newValue;
    }

    setCanShowTax = (newValue: boolean) => {
        this.canShowTax = newValue;
    }

    get hasChanges() {
        return this.checkoutInput.locationSlotsCount !== this.initialCheckoutValues.purchasedLocationSlots
        || this.billableUserSeats !== this.initialCheckoutValues.purchasedUserSeats
        || this.checkoutInput.period !== this.initialCheckoutValues.period;
    }

    get billableUserSeats(): number {
        const planConfig = SeatManager.getPlanConfig(this.client.subscription_plan);
        const includedUsersFromLocations = this.checkoutInput.locationSlotsCount * planConfig.usersPerLocation;
        const totalIncludedUsers = planConfig.baseUsers + includedUsersFromLocations;

        return Math.max(0, this.checkoutInput.userSeatsCount - totalIncludedUsers);
    }

    get validationErrors(): ValidationError[] {
        const errors: ValidationError[] = [];

        if (this.checkoutInput.locationSlotsCount < this.seatUsage.locations.current) {
            errors.push({
                field: 'locations',
                message: intl.get('checkout.warning.locations', {locationUsage: this.seatUsage.locations.current})
                    .d('Cannot reduce location slots below current usage ({locationUsage}). You can archive or delete locations to reduce the billable amount.'),
                canProceed: false,
            });
        }

        if (this.checkoutInput.userSeatsCount < this.seatUsage.users.current) {
            errors.push({
                field: 'users',
                message: intl.get('checkout.warning.users', {usersUsage: this.seatUsage.users.current})
                    .d('Cannot reduce user seats below current usage ({usersUsage}). You can delete users to reduce the billable amount.'),
                canProceed: false,
            });
        }

        return errors;
    }

    get canProceed(): boolean {
        return this.validationErrors.every(error => error.canProceed);
    }

    setCheckoutInput(newData: Partial<CheckoutInput>): void {
        this.checkoutInput = {
            ...this.checkoutInput,
            ...newData,
        };

        this.recalculatePricing();
    }

    protected setTotals(totals: PriceDetails, items: CheckoutEventsItem[] = []): void {
        this.totals = totals;
        this.items = items;
    }

    protected configureInitialState(): void {
        // Subclasses can override to customise before initial calculation
    }

    protected recalculatePricing(): void {
        const pricing = this.buildPricingComputation();
        const processedPricing = this.processPricing(pricing);
        this.setTotals(processedPricing.priceDetails, processedPricing.items);
        this.refreshProration();
    }

    protected buildPricingComputation(): PricingComputation {
        const {period, locationSlotsCount} = this.checkoutInput;

        if (this.priceCurrency === 'USD') {
            return calculateUsdPricing({
                period,
                locationSlotsCount,
                billableUserSeats: this.billableUserSeats,
            });
        }

        return calculateRubPricing({
            period,
            locationSlotsCount,
            billableUserSeats: this.billableUserSeats,
        });
    }

    protected processPricing(pricing: PricingComputation): PricingComputation {
        return pricing;
    }

    protected refreshProration(): void {
        if (!this.isUpgradeFlow || !this.hasChanges) {
            this.setProrationError(null);
            this.setProrationSummary(null);
            this.setProrationLoading(false);
            return;
        }

        const requestId = ++this.prorationRequestId;
        this.setProrationLoading(true);
        this.setProrationError(null);

        this.fetchProrationPreview(this.userState.wsServerConnection)
            .then((summary) => {
                if (requestId !== this.prorationRequestId) {
                    return;
                }
                this.setProrationSummary(summary);
            })
            .catch((error: unknown) => {
                if (requestId !== this.prorationRequestId) {
                    return;
                }
                logErrorToSentry(error as Error | string);
                this.setProrationError(error instanceof Error ? error.message : String(error));
                // For upgrade flows, we need real proration data - no fallback
                this.setProrationSummary(null);
            })
            .finally(() => {
                if (requestId === this.prorationRequestId) {
                    this.setProrationLoading(false);
                }
            });
    }

    protected async fetchProrationPreview(wsServerConnection: WsServerConnection): Promise<ProrationSummary | null> {
        const periodInfo = this.client?.subscription_details?.current_billing_period;
        if (!periodInfo) {
            // No active billing period => nothing to prorate
            return null;
        }

        // Compute remaining ratio in the current billing period
        const startsAt = new Date(periodInfo.starts_at);
        const endsAt = new Date(periodInfo.ends_at);
        const now = new Date();
        const totalMs = Math.max(0, endsAt.getTime() - startsAt.getTime());
        const remainingMs = Math.max(0, endsAt.getTime() - now.getTime());

        let remainingRatio = 1;
        if (totalMs > 0 && remainingMs > 0) {
            remainingRatio = remainingMs / totalMs;
        }


        // Build pricing for CURRENT desired quantities
        const newPricing = this.buildPricingComputation();

        // Build pricing for ALREADY PURCHASED quantities
        const oldLocationSlots = this.initialCheckoutValues.purchasedLocationSlots;
        const oldBillableUserSeats = this.initialCheckoutValues.purchasedUserSeats;
        const period = this.checkoutInput.period;
        const oldPricing = this.priceCurrency === 'USD'
            ? calculateUsdPricing({
                period,
                locationSlotsCount: oldLocationSlots,
                billableUserSeats: oldBillableUserSeats,
            })
            : calculateRubPricing({
                period,
                locationSlotsCount: oldLocationSlots,
                billableUserSeats: oldBillableUserSeats,
            });

        // Compute delta between new and old for one full period, then prorate to remaining time
        const deltaSubtotalFull = Math.max(0, newPricing.priceDetails.subtotal - oldPricing.priceDetails.subtotal);
        const deltaTotalFull = Math.max(0, newPricing.priceDetails.total - oldPricing.priceDetails.total);
        const deltaTaxFull = Math.max(0, (newPricing.priceDetails.tax || 0) - (oldPricing.priceDetails.tax || 0));

        const proratedSubtotal = deltaSubtotalFull * remainingRatio;
        const proratedTax = deltaTaxFull * remainingRatio;
        const proratedTotal = deltaTotalFull * remainingRatio;

        // Expose inferred proration period in summary
        const proration: ProrationSummary['proration'] = {
            startsAt: periodInfo.starts_at,
            endsAt: periodInfo.ends_at,
            rate: remainingRatio,
        };

        return {
            currency: this.priceCurrency,
            totals: {
                subtotal: proratedSubtotal,
                tax: proratedTax,
                total: proratedTotal,
            },
            proration,
        };
    }

    protected setProrationSummary(summary: ProrationSummary | null): void {
        this.prorationDueNow = summary;
        if (summary && summary.currency !== this.priceCurrency) {
            this.priceCurrency = summary.currency;
        }
    }

    protected setProrationLoading(value: boolean): void {
        this.isProrationLoading = value;
    }

    protected setShowSuccessPayment(value: boolean): void {
        this.showSuccessPayment = value;
    }

    protected setProcessingUpgrade(value: boolean): void {
        this.isProcessingUpgrade = value;
    }

    protected setProrationError(message: string | null): void {
        this.prorationError = message;
    }


    get isUpgradeFlow(): boolean {
        return !!(this.client?.subscription_details?.id &&
            this.client?.subscription_status === 'active' &&
            this.client?.subscription_details?.current_billing_period);
    }

    async handleUpgrade() {
        return false;
    }
}
